import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Twitter, Linkedin, Github } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

const teamMembers = [
  {
    name: 'Mr Hudson',
    role: 'Founder & CEO',
    image: 'https://customer-assets.emergentagent.com/job_polk-blockchain-next/artifacts/3rw9vlo6_POLK%20CEO.png',
    socials: { twitter: '#', linkedin: '#', github: '#' },
  },
  {
    name: 'Sarah Williams',
    role: 'CTO',
    image: 'https://images.pexels.com/photos/28442317/pexels-photo-28442317.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    socials: { twitter: '#', linkedin: '#', github: '#' },
  },
  {
    name: 'Michael Park',
    role: 'Head of Engineering',
    image: 'https://images.unsplash.com/photo-1737574821698-862e77f044c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxwcm9mZXNzaW9uYWwlMjBjb3Jwb3JhdGUlMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzUzODc1Mzd8MA&ixlib=rb-4.1.0&q=85',
    socials: { twitter: '#', linkedin: '#', github: '#' },
  },
  {
    name: 'Emma Rodriguez',
    role: 'Head of Product',
    image: 'https://images.pexels.com/photos/36645466/pexels-photo-36645466.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    socials: { twitter: '#', linkedin: '#', github: '#' },
  },
];

export const Team = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const { isDark } = useTheme();

  return (
    <section
      id="team"
      className={`py-24 sm:py-32 relative ${isDark ? 'bg-[#0a0a0a]' : 'bg-[#F1F5F9]'}`}
      ref={ref}
      data-testid="team-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-xs sm:text-sm uppercase tracking-[0.2em] font-medium text-blue-500 mb-4 block">
            Our Team
          </span>
          <h2 className={`text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Meet the <span className="gradient-text">Visionaries</span>
          </h2>
          <p className={`text-base sm:text-lg max-w-2xl mx-auto ${isDark ? 'text-zinc-400' : 'text-slate-600'}`}>
            World-class experts building the future of decentralized technology.
          </p>
        </motion.div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {teamMembers.map((member, index) => (
            <motion.div
              key={member.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="group"
            >
              <div className={`relative overflow-hidden rounded-3xl border transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl ${
                isDark 
                  ? 'bg-white/[0.03] border-white/10' 
                  : 'bg-white border-black/5'
              }`}>
                {/* Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className={`text-xl font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    {member.name}
                  </h3>
                  <p className="text-blue-400 text-sm mb-4">{member.role}</p>

                  {/* Socials */}
                  <div className="flex gap-3">
                    <a
                      href={member.socials.twitter}
                      className={`p-2 rounded-lg transition-colors ${
                        isDark 
                          ? 'bg-white/10 hover:bg-white/20' 
                          : 'bg-black/5 hover:bg-black/10'
                      }`}
                    >
                      <Twitter className={`w-4 h-4 ${isDark ? 'text-white' : 'text-gray-700'}`} />
                    </a>
                    <a
                      href={member.socials.linkedin}
                      className={`p-2 rounded-lg transition-colors ${
                        isDark 
                          ? 'bg-white/10 hover:bg-white/20' 
                          : 'bg-black/5 hover:bg-black/10'
                      }`}
                    >
                      <Linkedin className={`w-4 h-4 ${isDark ? 'text-white' : 'text-gray-700'}`} />
                    </a>
                    <a
                      href={member.socials.github}
                      className={`p-2 rounded-lg transition-colors ${
                        isDark 
                          ? 'bg-white/10 hover:bg-white/20' 
                          : 'bg-black/5 hover:bg-black/10'
                      }`}
                    >
                      <Github className={`w-4 h-4 ${isDark ? 'text-white' : 'text-gray-700'}`} />
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
